<template>
  <div class="general-form">
    <FormTitle title="General info" />
    <a-form-model :model="form" ref="form" class="general-form__container">
      <div>
        <FormSubTitle title="Inner info" />
        <div class="d-flex flex-column">
          <Readonly
            :show="isOnlyView && !!form.name"
            title="Inner name"
            :value="form.name"
            style="max-width: 270px; align-self: flex-start;"
            class="mb-3"
          >
            <a-form-model-item
              label="Inner name"
              prop="name"
              :colon="false"
              :rules="[{ required: true, message: 'Inner name is required', trigger: ['blur', 'change'] }]"
              class="mr-5"
              style="max-width: 270px"
            >
              <a-input
                v-model="form.name"
                style="width: 270px;"
                placeholder="Inner name"
                size="large"
                allow-clear
                :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView }"
                :disabled="loading || isOnlyView"
              />
            </a-form-model-item>
          </Readonly>
          <Readonly
            :show="isOnlyView && !!form.description"
            title="Inner description"
            :value="form.description"
            style="max-width: 270px; align-self: flex-start;"
          >
            <a-form-model-item
              label="Inner description"
              prop="description"
              :colon="false"
              class="mr-5"
              style="max-width: 270px"
            >
              <a-textarea
                v-model="form.description"
                size="large"
                style="min-width: 270px; width: 270px;"
                placeholder="Inner description"
                :auto-size="{ minRows: 2, maxRows: 6 }"
                :disabled="loading || isOnlyView"
              />
            </a-form-model-item>
          </Readonly>
        </div>
        <FormSubTitle title="General settings" :class="{'mt-3': isOnlyView}" />
        <div class="d-flex flex-column" style="max-width: 500px;">
          <div class="card-container mb-3">
            <a-tabs type="card">
              <a-tab-pane
                v-for="(translation, translationIndex) in form.translations"
                :key="translationIndex"
                force-render
              >
                <template #tab>
                  <DefaultLabelTab
                    :name="translation.tab_name"
                    :is_default="translation.is_default"
                    :error_validate="form.translations[translationIndex].error_validate"
                  />
                </template>
                <Readonly
                  :show="isOnlyView"
                  title="Name"
                  :value="form.translations[translationIndex].name"
                  :required="true"
                  style="width: 300px;"
                >
                  <a-form-model-item
                    style="width: 300px"
                    label="Name"
                    :key="`name${translationIndex}`"
                    :prop="`translations.${translationIndex}.name`"
                    :colon="false"
                    :rules="[
                      { required: true, message: 'Name is required', trigger: ['blur', 'change'] },
                    ]"
                  >
                    <a-input
                      v-model="form.translations[translationIndex].name"
                      placeholder="Name"
                      size="large"
                      :disabled="loading"
                      @focus="clearError(translationIndex)"
                    />
                  </a-form-model-item>
                </Readonly>
              </a-tab-pane>
            </a-tabs>
          </div>
          <a-row class="d-flex align-items-center" :class="{'mb-4' : isOnlyView}">
            <Readonly
              :show="isOnlyView && !!form.started_at"
              title="Start date"
              :value="form.started_at"
              :isDate="true"
              style="max-width: 270px; align-self: flex-start;"
            >
              <a-form-model-item
                label="Start date"
                prop="started_at"
                :colon="false"
                style="max-width: 270px"
                :rules="[
                  { required: true, message: 'Start date is required', trigger: ['change'] },
                  { validator: validateStartDate, trigger: ['blur', 'change'] },
                ]"
              >
                <a-date-picker
                  v-model="form.started_at"
                  size="large"
                  format="YYYY-MM-DD HH:mm"
                  :disabled="loading || isOnlyView"
                  :disabled-date="disableDatesBeforeToday"
                  :disabled-time="disabledTimeBeforeToday"
                  :show-time="{
                    defaultValue: $moment('00:00:00', 'HH:mm'),
                    format: 'HH:mm'
                  }"
                  class="hover-date-picker"
                />
              </a-form-model-item>
            </Readonly>
            <span class="mx-3" :class="{'align-self-end mb-1' : isOnlyView && !!form.started_at && !!form.expires_at}">—</span>
            <Readonly
              :show="isOnlyView && !!form.expires_at"
              title="End date"
              :value="form.expires_at"
              :isDate="true"
              style="max-width: 270px; align-self: flex-start;"
            >
              <a-form-model-item
                label="End date"
                prop="expires_at"
                :colon="false"
                style="max-width: 270px"
                :rules="[{ required: true, message: 'End date is required', trigger: ['change'] }]"
              >
                <a-date-picker
                  v-model="form.expires_at"
                  size="large"
                  format="YYYY-MM-DD HH:mm"
                  :disabled="loading || isOnlyView"
                  :disabled-date="disabledEndDate"
                  :disabled-time="disabledEndTime"
                  :show-time="{
                    defaultValue: $moment('00:00:00', 'HH:mm'),
                    format: 'HH:mm'
                  }"
                  class="hover-date-picker"
                />
              </a-form-model-item>
            </Readonly>
          </a-row>
          <a-form-model-item
            prop="landing_link"
            label="Landing link"
            :rules="[{ required: true, message: 'Landing link is required', trigger: ['blur', 'change'] }]"
            :colon="false"
          >
            <UniversalSelect
              v-model="form.landing_link"
              style="width: 100%;"
              :options="landingOptions"
              :showSearch="true"
              :readonly="isOnlyView"
              placeholder="Select landing link"
              showCopyIcon
              :disabled="loading || isOnlyView || getLandingLinksLoading"
              @copy="copy($event, 'Landing link copied to clipboard')"
            />
          </a-form-model-item>
          <a-form-model-item
            prop="conditions_link"
            label="Conditions link"
            :rules="[
              { max: 255, message: 'Maximum 255 characters' },
              { required: true, message: 'Conditions link is required', trigger: ['blur', 'change'] },
              { validator: validateConditionsLink, trigger: ['blur'] },
            ]"
            :colon="false"
            class="label-with-tooltip"
          >
            <a-input
              v-model="form.conditions_link"
              style="width: 100%"
              placeholder="https://"
              allowClear
              size="large"
              :class="{ 'transparent-input transparent-input__view-mode' : isOnlyView && form.conditions_link.length }"
              :disabled="loading || isOnlyView"
            >
              <a-icon
                slot="addonBefore"
                type="copy"
                class="font-size-18"
                :class="form.conditions_link.length ? 'text-primary cursor-pointer' : 'text-default cursor-default'"
                @click="copy(form.conditions_link, 'Conditions link copied to clipboard')"
              />
            </a-input>
          </a-form-model-item>
        </div>
      </div>
      <div>
        <FormSubTitle title="Apps and languages" />
        <a-row style="width: 100%;">
          <a-form-model-item prop="applications" required :colon="false" class="d-block">
            <template #label>
              <span class="mr-4">Apps</span>
              <span v-if="availableApplications.length && !isOnlyView" class="add-link cursor-pointer" @click.stop="toggleShowAppsModal(true)">+ Add</span>
            </template>
            <div v-if="form.applications">
              <a-tag
                v-for="app in form.applications.slice(0, 5)"
                :key="app.id"
                style="
                  margin-right: 15px;
                  padding: 1px 8px;
                  border-radius: 2px;
                "
                class="cursor-pointer"
                @click="toggleShowAppsModal(true)"
              >
                {{ app.name }}
                <a-icon v-if="app?.platform?.toLowerCase() === 'android'" type="android" class="ml-1 text-success" />
                <a-icon v-if="app?.platform?.toLowerCase() === 'ios'" type="apple" class="ml-1 text-primary" />
                <a-icon v-if="app?.platform?.toLowerCase() === 'web'" type="global" class="ml-1 text-default" />
              </a-tag>
              <a-tag v-if="form.applications.length > 4"> ... </a-tag>
            </div>
          </a-form-model-item>
        </a-row>
        <a-row>
          <a-form-model-item prop="languages" required :colon="false" class="d-block">
            <template #label>
              <span class="mr-4">Languages</span>
              <span v-if="!isOnlyView" class="add-link cursor-pointer" @click.stop="toggleShowEditLanguagesModal(true)">+ Add</span>
            </template>
            <div class="general-form__languages">
              <div
                v-for="(language, index) in form.languages"
                :key="language.id"
                class="general-form__language"
                :class="{'cursor-pointer' : !isOnlyView}"
              >
                <div>
                  <span class="mr-2">{{ language.name }}</span>
                  <a-badge
                    v-if="language.is_default"
                    count="Default"
                    :number-style="{
                      backgroundColor: '#fff',
                      color: '#999',
                      boxShadow: '0 0 0 1px #d9d9d9 inset',
                    }"
                    style="transform: scale(1.2);"
                  />
                </div>
                <div v-if="!isOnlyView">
                  <a-popconfirm
                    v-if="!language.is_default"
                    placement="left"
                    :disabled="loading"
                    title="Are you sure you want to delete this language?"
                    @confirm="() => deleteLanguage(language, index)"
                  >
                    <a-icon class="font-size-18 text-danger delete-icon" type="delete" />
                  </a-popconfirm>
                </div>
              </div>
            </div>
          </a-form-model-item>
        </a-row>
      </div>
    </a-form-model>

    <a-modal
      v-model="showAppsModal"
      title="Add new apps"
      :footer="null"
      width="700px"
      :destroyOnClose="true"
    >
      <AppsModal
        :modalAppsData="form.applications"
        :availableApplications="availableApplications"
        :readonly="isOnlyView"
        @selectApps="changeSelectedApps"
        @close="toggleShowAppsModal(false)"
      />
    </a-modal>

    <EditLanguagesModal
      v-if="showEditLanguagesModal"
      :show="showEditLanguagesModal"
      :allLanguages="availableLanguages"
      :languagesUsed="languagesUsed"
      @update="updateLanguages"
      @close="toggleShowEditLanguagesModal"
    />
  </div>
</template>

<script>
import FormTitle from '@/components/custom/form/FormTitle.vue'
import FormSubTitle from '@/components/custom/form/FormSubTitle.vue'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import Readonly from '@/components/custom/readonlyMode/readonly.vue'
import EditLanguagesModal from '@/views/apps/referral-program/components/EditLanguagesModal.vue'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import { copyToClipboard } from '@/composables/copy/copyToClipboard.js'
import { getReferralLandingLinks } from '@/views/apps/referral-program/composables/useGetReferralLandingLinks.js'

export default {
  props: {
    defaultLanguage: {
      type: Object,
    },
    availableLanguages: {
      type: Array,
      default: () => [],
    },
    availableApplications: {
      type: Array,
      default: () => [],
    },
    isOnlyView: {
      type: Boolean,
      default: false,
    },
    errorFormValidate: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FormTitle,
    FormSubTitle,
    DefaultLabelTab,
    Readonly,
    UniversalSelect,
    AppsModal,
    EditLanguagesModal,
  },
  data: () => ({
    programId: null,
    form: {
      name: '',
      description: '',
      started_at: '',
      expires_at: '',
      landing_link: '',
      conditions_link: '',
      translations: [],
      applications: [],
      languages: [],
    },
    landingOptions: [],
    showAppsModal: false,
    showEditLanguagesModal: false,
    getLandingLinksLoading: false,
  }),
  watch: {
    form: {
      async handler() {
        if (!this.errorFormValidate) {
          return
        }
        const isValidGeneralInfoForm = await this.validateFields()
        if (!isValidGeneralInfoForm) {
          return
        }
        this.$emit('clearError')
      },
      deep: true,
    },
    languagesUsed: {
      handler(val) {
        if (!val) {
          return
        }
        this.changeFormTranslations()
        this.$emit('changeLanguagesUsed', val)
      },
    },
  },
  computed: {
    isEditMode() {
      return !!this.programId
    },
    languagesUsed() {
      if (!this.form.languages?.length) {
        return []
      }
      return Object.values(this.form.languages).map(lang => ({
        name: lang.name,
        value: lang.id,
        is_default: lang.is_default,
      }))
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    validateStartDate(rule, value, callback) {
      if (!value) {
        return callback() // required валидатор уже отработает
      }

      const now = this.$moment().startOf('day') // Сегодня с 00:00
      const selected = this.$moment(value).startOf('day')

      if (selected.isBefore(now)) {
        return callback(new Error('Start date cannot be in the past'))
      }

      callback()
    },
    validateConditionsLink(rule, value, callback) {
      try {
        const url = new URL(value)
        if (!url.protocol.startsWith('https')) {
          return callback(new Error('URL must start with https://'))
        }
        return callback()
      } catch (e) {
        return callback(new Error('Invalid URL'))
      }
    },
    setDefaultValues() {
      this.form.languages.push(this.defaultLanguage)
      this.addTranslation(this.defaultLanguage)
    },
    async setFormValues(data) {
      const {
        name,
        description,
        started_at,
        expires_at,
        languages = [],
        translations = [],
        applications = [],
        landing_link,
        conditions_link,
      } = data

      this.form.name = name
      this.form.description = description
      this.form.started_at = started_at ? this.$moment(started_at) : undefined
      this.form.expires_at = expires_at ? this.$moment(expires_at) : undefined
      this.form.conditions_link = conditions_link || ''

      languages.forEach(lang => {
        const langName = this.getLangName(lang.id)
        this.form.languages.push({ ...lang, name: langName })
        const translationName = translations.find(translation => translation.id === lang.id)?.name
        if (!translationName) {
          return
        }
        this.addTranslation({ ...lang, name: langName }, translationName)
      })
      applications.forEach(app => {
        const fullAppInfo = this.getApplication(app.id)
        if (!fullAppInfo) {
          return
        }
        this.form.applications.push({
          ...fullAppInfo,
          application_id: app.id,
          min_app_version: app.from,
          max_app_version: app.to,
        })
      })
      // Убедимся, что landingOptions загружены
      if (!this.landingOptions.length) {
        await this.waitForLandingOptions()
      }
      this.form.landing_link = landing_link ? this.getLandingId(landing_link) : ''
    },
    getLangName(langId) {
      return this.availableLanguages.find(lang => lang.id === langId)?.name
    },
    getApplication(appId) {
      return this.availableApplications.find(app => app.application_id === appId)
    },
    toggleShowAppsModal(status = false) {
      this.showAppsModal = status
    },
    toggleShowEditLanguagesModal(status = false) {
      this.showEditLanguagesModal = status
    },
    updateLanguages(newLanguages) {
      const newLangIds = newLanguages.map(lang => lang.value)

      // Найдём язык с is_default === true (может быть только один)
      const defaultLangId = newLanguages.find(lang => lang.is_default)?.value

      // Фильтруем старые языки — оставляем только те, что есть в новом списке
      const updatedLanguages = (this.form.languages || []).filter(lang => {
        if (newLangIds.includes(lang.id)) {
          return true
        }
        this.removeTranslation(lang.id)
        return false
      })

      // Обновляем is_default для уже существующих языков
      updatedLanguages.forEach(lang => {
        lang.is_default = lang.id === defaultLangId
      })

      // Добавляем новые, которых ещё не было
      newLanguages.forEach(newLang => {
        const alreadyExists = updatedLanguages.some(lang => lang.id === newLang.value)
        if (!alreadyExists) {
          updatedLanguages.push({
            id: newLang.value,
            name: newLang.name,
            is_default: newLang.is_default,
          })

          this.addTranslation({
            id: newLang.value,
            name: newLang.name,
            is_default: newLang.is_default,
          })
        }
      })

      this.form.languages = updatedLanguages
    },
    deleteLanguage(lang, langIdx) {
      this.form.languages.splice(langIdx, 1)
      this.removeTranslation(lang.id)
      if (lang.is_default && this.form.languages.length) {
        this.form.languages[0].is_default = true
      }
    },
    changeFormTranslations() {
      const languageIds = this.form.languages.map(lang => lang.id)
      const defaultLangId = this.form.languages.find(lang => lang.is_default)?.id

      // Удаляем переводы, которых больше нет в языках
      this.form.translations = this.form.translations.filter(translation => {
        return languageIds.includes(translation.id)
      })

      const updatedTranslations = this.form.languages.map(lang => {
        const existing = this.form.translations.find(t => t.id === lang.id)
        return {
          id: lang.id,
          name: existing?.name || '',
          tab_name: lang.name,
          is_default: lang.id === defaultLangId,
          error_validate: existing?.error_validate || false,
        }
      })

      // Ставим перевод is_default первым
      updatedTranslations.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))

      this.form.translations = updatedTranslations
    },
    addTranslation(lang, name = '') {
      this.form.translations.push({
        id: lang.id,
        name,
        tab_name: lang.name,
        is_default: lang.is_default,
        error_validate: false,
      })
    },
    removeTranslation(langId) {
      this.form.translations = this.form.translations.filter(translation => translation.id !== langId)
    },
    changeSelectedApps(apps) {
      this.toggleShowAppsModal(false)
      this.form.applications = apps
      this.validateApps()
    },
    async validateApps() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('applications', resolve)
      })
      return isValid
    },
    disableDatesBeforeToday(startValue) {
      const endValue = this.form.expires_at
      if (!startValue || !endValue) {
        return startValue && startValue < this.$moment().add(-1, 'day').endOf('day')
      }
      return startValue.valueOf() > endValue.valueOf() || startValue < this.$moment().add(-1, 'day').endOf('day')
    },
    disabledTimeBeforeToday(startValue) {
      if (!startValue) {
        return {}
      }
      const now = this.$moment()

      // если выбран сегодня — запретим все часы/минуты до текущего момента
      if (startValue.isSame(now, 'day')) {
        const disabledHours = []
        for (let i = 0; i < now.hour(); i++) {
          disabledHours.push(i)
        }
        const disabledMinutes = []
        if (startValue.hour() === now.hour()) {
          for (let i = 0; i < now.minute(); i++) {
            disabledMinutes.push(i)
          }
        }
        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return {}
    },
    disabledEndDate(endValue) {
      const startValue = this.form.started_at
      if (!endValue || !startValue) {
        return endValue && endValue < this.$moment().add(-1, 'day').endOf('day')
      }
      if (startValue.isSame(endValue, 'day')) {
        return false
      }
      return startValue.valueOf() >= endValue.valueOf()
    },
    disabledEndTime(endValue) {
      const startValue = this.form.started_at
      if (!endValue || !startValue) {
        return {}
      }

      // если start и end один день → ограничиваем время
      if (startValue.isSame(endValue, 'day')) {
        const disabledHours = []
        for (let i = 0; i < startValue.hour(); i++) {
          disabledHours.push(i)
        }
        const disabledMinutes = []
        if (endValue.hour() === startValue.hour()) {
          for (let i = 0; i < startValue.minute(); i++) {
            disabledMinutes.push(i)
          }
        }
        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return {}
    },
    copy(value, message) {
      copyToClipboard(value, message)
    },
    getPayload() {
      const payload = {}
      payload.name = this.form.name
      payload.description = this.form.description
      payload.started_at = this.form.started_at ? this.form.started_at.tz(this.$moment.tz.guess()).format() : null
      payload.expires_at = this.form.expires_at ? this.form.expires_at.tz(this.$moment.tz.guess()).format() : null
      payload.landing_link = this.getLandingUrl(this.form.landing_link)
      payload.conditions_link = this.form.conditions_link
      payload.applications = this.form.applications.map(app => {
        return { id: app.application_id, from: app.min_app_version || '0.0.0', to: app.max_app_version || '999.999.999' }
      })
      payload.languages = this.form.languages.map(lang => {
        return { id: lang.id, is_default: lang.is_default }
      })
      payload.translations = this.form.translations.map(translation => {
        return { language_id: translation.id, name: translation.name }
      })
      return payload
    },
    getLandingId(landingUrl) {
      return this.landingOptions.find(option => option.name === landingUrl)?.value
    },
    getLandingUrl(landingId) {
      return this.landingOptions.find(option => option.value === landingId)?.name
    },
    checkingForGeneralTabFilling() {
      let isValid = true

      this.form.translations.forEach((_, index) => {
        if (!this.form.translations[index].name?.length) {
          this.form.translations[index].error_validate = true
          isValid = false
        } else {
          this.form.translations[index].error_validate = false
        }
      })
      return isValid
    },
    async validateFields() {
      // Делаем чтобы подсветить в каких табах ошибки
      this.checkingForGeneralTabFilling()

      return new Promise((resolve) => {
        this.$refs.form.validate((valid) => {
          resolve(valid)
        })
      })
    },
    clearError(translationsIndex) {
      this.form.translations[translationsIndex].error_validate = false
    },
    async getLandingsLinks() {
      try {
        this.getLandingLinksLoading = true
        const options = await getReferralLandingLinks() || []
        if (!options.length) {
          return
        }
        this.landingOptions = options.map(option => {
          return {
            name: option.url,
            value: option.id,
          }
        })
      } finally {
        this.getLandingLinksLoading = false
      }
    },
    waitForLandingOptions() {
      return new Promise((resolve) => {
        const check = () => {
          if (this.landingOptions.length) {
            resolve()
          } else {
            setTimeout(check, 100)
          }
        }
        check()
      })
    },
  },
  mounted() {
    this.programId = this.$route.params.id

    this.getLandingsLinks()
    if (this.isEditMode) {
      return
    }
    this.setDefaultValues()
  },
}
</script>

<style lang="scss">
.general-form {
  &__container {
    display: flex;
    flex-wrap: wrap;
    gap: 200px;
    @media (max-width: 768px) {
      gap: 20px;
    }
  }
    &__languages {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  &__language {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 38px;
    border: 1px solid #e4e9f0;
    border-radius: 4px;
    max-width: 400px;
    min-width: 270px;
    padding: 0 10px;
    &:hover {
      border-color: #40a9ff;
    }
    & > div {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    &__set-default {
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }
  .add-link {
    color: #007bff;
    font-weight: 400;
  }
  .hover-date-picker {
    & input {
      &:hover {
        cursor: pointer !important;
      }
    }
  }
  .card-container {
    overflow: hidden;
  }
  .card-container > .ant-tabs-card > .ant-tabs-content {
    margin-top: -16px;
  }

  .card-container > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    background: rgba(0, 123, 255, 0.05) !important;
    padding: 16px;
  }

  .card-container > .ant-tabs-card > .ant-tabs-bar {
    border-color: rgba(0, 123, 255, 0.05) !important;
  }

  .card-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    border-color: transparent;
    background: transparent;
    margin-right: 0 !important;
  }

  .card-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab-active {
    border-color: rgba(0, 123, 255, 0.05) !important;
    background: rgba(0, 123, 255, 0.05) !important;
  }
}
</style>
